import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { displayPrice } from '../../../util/configHelpers';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
  STOCK_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureOwnListing } from '../../../util/data';
import {
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  createCategoryForUrl,
} from '../../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../../util/routes';
import { isBookingProcessAlias, isPurchaseProcessAlias } from '../../../transactions/transaction';
import {
  AspectRatioWrapper,
  InlineTextButton,
  NamedLink,
  IconSpinner,
  PrimaryButtonInline,
  ResponsiveImage,
} from '../../../components';
import Overlay from './Overlay';
import ManageListingStatus from './ManageListingStatus';
import { LISTING_STATUS } from '../../../constants/listing.constants';
import ListingPromotion from './ListingPromotion';
import ListingStatus from './ListingStatus';
import { PROMOTION_TYPE } from '../../../constants/payment.constants';
import { getPromotionPlanType } from '../../../components/PromotionModal/PromotionModal';
import useInterfaceLang from '../../../hooks/useInterfaceLang';
import { formatLocation } from '../../../util/mapbox';

import css from './ManageListingCard.module.css';

// Menu content needs the same padding
const MAX_LENGTH_FOR_WORDS_IN_TITLE = 7;

const priceData = (price, currency, intl) => {
  if (price?.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

function stripYearFromTitle(title) {
  return title.replace(/\b(17|18|19|20)\d{2}\b$/, '').trim();
}

const createLinkProps = listing => {
  const id = listing.id.uuid;
  const slug = createSlug(listing.attributes.title);
  const category = createCategoryForUrl(listing.attributes.publicData.category);
  const isPendingApproval = listing.attributes.state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = listing.attributes.state === LISTING_STATE_DRAFT;
  const variant = isDraft
    ? LISTING_PAGE_DRAFT_VARIANT
    : isPendingApproval
    ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
    : null;

  return isPendingApproval || isDraft
    ? {
        name: 'ListingPageVariant',
        params: {
          id,
          slug,
          category,
          variant,
          lang,
        },
      }
    : {
        name: 'ListingPage',
        params: { id, slug, category },
      };
};

const createListingURL = (routes, listing) => {
  const linkProps = createLinkProps(listing);

  return createResourceLocatorString(linkProps.name, routes, linkProps.params, {});
};

// Cards are not fixed sizes - So, long words in title make flexboxed items to grow too big.
// 1. We split title to an array of words and spaces.
//    "foo bar".split(/([^\s]+)/gi) => ["", "foo", " ", "bar", ""]
// 2. Then we break long words by adding a '<span>' with word-break: 'break-all';
const formatTitle = (title, maxLength) => {
  const nonWhiteSpaceSequence = /([^\s]+)/gi;
  return title.split(nonWhiteSpaceSequence).map((word, index) => {
    return word.length > maxLength ? (
      <span key={index} style={{ wordBreak: 'break-all' }}>
        {word}
      </span>
    ) : (
      word
    );
  });
};

const ShowClosedOverlayMaybe = props => {
  const {
    isClosed,
    actionsInProgressListingId,
    currentListingId,
    currentListingState,
    currentListingStatus,
    onOpenListing,
  } = props;

  const isSold = currentListingStatus === LISTING_STATUS.SOLD;
  const isCanceled = currentListingStatus === LISTING_STATUS.CANCELED;

  return isClosed ? (
    <Overlay>
      <PrimaryButtonInline
        className={classNames(css.openListingButton, {
          [css.openListingButtonPrevented]: isSold || isCanceled,
        })}
        disabled={isSold || isCanceled || !!actionsInProgressListingId}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          if (!actionsInProgressListingId) {
            onOpenListing(currentListingId, currentListingState);
          }
        }}
      >
        {isSold ? (
          <FormattedMessage id="General.listing.status.sold" />
        ) : isCanceled ? (
          <FormattedMessage id="General.listing.status.canceled" />
        ) : (
          <FormattedMessage id="ManageListingCard.reOpenListing" />
        )}
      </PrimaryButtonInline>
    </Overlay>
  ) : null;
};

const ShowPendingApprovalOverlayMaybe = props => {
  const { isPendingApproval, title, intl } = props;

  return isPendingApproval ? (
    <Overlay
      message={intl.formatMessage(
        { id: 'ManageListingCard.pendingApproval' },
        { listingTitle: title }
      )}
    />
  ) : null;
};

const ShowOutOfStockOverlayMaybe = props => {
  const {
    showOutOfStockOverlay,
    title,
    id,
    slug,
    actionsInProgressListingId,
    currentListingId,
    hasStockManagementInUse,
    onCloseListing,
    intl,
  } = props;

  return showOutOfStockOverlay ? (
    <Overlay
      message={intl.formatMessage(
        { id: 'ManageListingCard.outOfStockOverlayText' },
        { listingTitle: title }
      )}
    >
      {hasStockManagementInUse ? (
        <>
          <NamedLink
            className={css.finishListingDraftLink}
            name="EditListingPage"
            params={{ id, slug, type: LISTING_PAGE_PARAM_TYPE_EDIT, tab: 'pricing-and-stock' }}
          >
            <FormattedMessage id="ManageListingCard.setPriceAndStock" />
          </NamedLink>

          <div className={css.alternativeActionText}>
            {intl.formatMessage(
              { id: 'ManageListingCard.closeListingTextOr' },
              {
                closeListingLink: (
                  <InlineTextButton
                    key="closeListingLink"
                    className={css.alternativeActionLink}
                    disabled={!!actionsInProgressListingId}
                    onClick={() => {
                      if (!actionsInProgressListingId) {
                        onCloseListing(currentListingId);
                      }
                    }}
                  >
                    <FormattedMessage id="ManageListingCard.closeListingText" />
                  </InlineTextButton>
                ),
              }
            )}
          </div>
        </>
      ) : (
        <div className={css.alternativeActionText}>
          <InlineTextButton
            key="closeListingLink"
            className={css.alternativeActionText}
            disabled={!!actionsInProgressListingId}
            onClick={() => {
              if (!actionsInProgressListingId) {
                onCloseListing(currentListingId);
              }
            }}
          >
            <FormattedMessage id="ManageListingCard.closeListingText" />
          </InlineTextButton>
        </div>
      )}
    </Overlay>
  ) : null;
};

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(foundListingTypeConfig);
  if (showPrice && !price) {
    return (
      <div className={css.noPrice}>
        <FormattedMessage id="ManageListingCard.priceNotSet" />
      </div>
    );
  } else if (!showPrice) {
    return null;
  }
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ManageListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};

const Statistics = props => {
  const {
    viewsCount = '-',
    sentInquiriesCount = '-',
    viewsPhoneCount = '-',
    viewsWebsiteCount = '-',
    isPaused = false,
  } = props;
  return (
    <div className={classNames(css.statistics, { [css.statisticsPaused]: isPaused })}>
      <span className={css.statistic}>
        <span className={css.statisticData}>{viewsCount}</span>
        <FormattedMessage id="ManageListingCard.listingViews" />
      </span>
      <span className={css.statistic}>
        <span className={css.statisticData}>{sentInquiriesCount}</span>
        <FormattedMessage id="ManageListingCard.sentInquires" />
      </span>
      <span className={css.statistic}>
        <span className={css.statisticData}>{viewsWebsiteCount}</span>
        <FormattedMessage id="ManageListingCard.clicks" />
      </span>
      <span className={css.statistic}>
        <span className={css.statisticData}>{viewsPhoneCount}</span>
        <FormattedMessage id="ManageListingCard.phoneViews" />
      </span>
    </div>
  );
};

const ManageListingLinks = props => {
  const {
    id,
    slug,
    type,
    actionsInProgressListingId,
    currentListing,
    onDiscardDraft,
    isDraft,
    promotion,
    onOpenPromotionModal,
  } = props;

  const { publicData } = currentListing.attributes || {};
  const { status } = publicData || {};

  const isSold = status === LISTING_STATUS.SOLD;
  const isCanceled = status === LISTING_STATUS.CANCELED;
  const isForSale = status === LISTING_STATUS.FOR_SALE;

  return (
    <>
      {isForSale && (
        <InlineTextButton
          className={css.manageLink}
          onClick={() => {
            onOpenPromotionModal(id);
          }}
        >
          <FormattedMessage
            id={`ManageListingCard.${promotion?.plan ? 'managePromotion' : 'promote'}`}
          />
        </InlineTextButton>
      )}
      {!isSold && !isCanceled ? (
        <NamedLink
          className={css.manageLink}
          name="EditListingPage"
          params={{ id, slug, type, tab: 'details' }}
        >
          <FormattedMessage id="ManageListingCard.editListing" />
        </NamedLink>
      ) : null}

      {isDraft && (
        <InlineTextButton
          className={css.manageLink}
          key="discardDraftLink"
          disabled={!!actionsInProgressListingId}
          onClick={() => {
            if (!actionsInProgressListingId) {
              onDiscardDraft(currentListing.id);
            }
          }}
        >
          <FormattedMessage id="ManageListingCard.discardDraftLinkText" />
        </InlineTextButton>
      )}
    </>
  );
};

/**
 * Manage listing card
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.hasClosingError - Whether the closing error is present
 * @param {boolean} props.hasDiscardingError - Whether the discarding error is present
 * @param {boolean} props.hasOpeningError - Whether the opening error is present
 * @param {boolean} props.isMenuOpen - Whether the menu is open
 * @param {Object} [props.actionsInProgressListingId] - The actions in progress for the specific listing
 * @param {propTypes.uuid} [props.actionsInProgressListingId.uuid] - The uuid of the listing
 * @param {propTypes.ownListing} props.listing - The listing
 * @param {function} props.onCloseListing - The function to close the listing
 * @param {function} props.onOpenListing - The function to open the listing
 * @param {function} props.onDiscardDraft - The function to discard the draft
 * @param {string} [props.renderSizes] - The render sizes
 * @returns {JSX.Element} Manage listing card component
 */
export const ManageListingCard = props => {
  const { interfaceLang } = useInterfaceLang();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = props.intl || useIntl();
  const history = useHistory();

  const {
    className,
    rootClassName,
    relativeWrapperClassName,
    hasClosingError,
    hasDiscardingError,
    hasOpeningError,
    actionsInProgressListingId,
    listing,
    onCloseListing,
    onOpenListing,
    onDiscardDraft,
    onListingStatusChange,
    renderSizes,
    onManageDisableScrolling,
    onPromoteListing,
    onCancelListingPromotion,
    onOpenPromotionModal,
    enableBackgroundColorChange = false,
    disableOnClick = false,
    disableActions: disableActionsProp = false,
    hideStats = false,
    hideLabel = false,
    isExceedingSubscriptionLimit = false,
    isOnSearchPage = false,
  } = props;

  const currentListing = ensureOwnListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, state, publicData, metadata } = listing.attributes;
  const {
    viewsCount,
    sentInquiriesCount,
    viewsWebsiteCount,
    viewsPhoneCount,
    isPaused,
    promotionPlan,
    promotedUntil,
  } = metadata || {};
  const slug = createSlug(title);
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;
  const isActive = state === LISTING_STATE_PUBLISHED;

  const displayTitle = stripYearFromTitle(title);

  const isHighlightActive = [
    PROMOTION_TYPE.RECURRING.HIGHLIGHT,
    PROMOTION_TYPE.RECURRING.HIGHLIGHT_RECOMMENDED,
  ].includes(getPromotionPlanType(promotionPlan));

  const isRecommendedActive =
    !isHighlightActive &&
    [PROMOTION_TYPE.RECURRING.RECOMMENDED, PROMOTION_TYPE.RECURRING.HIGHLIGHT_RECOMMENDED].includes(
      getPromotionPlanType(promotionPlan)
    );

  const classes = classNames(rootClassName || css.root, className, {
    [css.rootAutoPaused]: isPaused,
  });

  const { listingType, transactionProcessAlias, yearBuilt, length, location: listingLocation } =
    publicData || {};

  const language = interfaceLang.split('-')[0];

  const address =
    typeof listingLocation?.address === 'object'
      ? formatLocation(listingLocation.address, language, true)
      : listingLocation?.address;

  const locationYearLengthInfo = [
    typeof length === 'number'
      ? `${length?.toFixed(2)}m`
      : typeof length === 'string'
      ? `${length}m`
      : null,
    yearBuilt,
    address,
  ]
    .filter(Boolean)
    .join(' | ');
  const isBookable = isBookingProcessAlias(transactionProcessAlias);
  const isProductOrder = isPurchaseProcessAlias(transactionProcessAlias);
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const disableActions = disableActionsProp || isPaused;

  const { author } = listing;
  const { companyName } = author?.attributes?.profile?.publicData || {};
  const { firstName, lastName, displayName: authorName } = author?.attributes?.profile || {};

  const displayName = companyName ?? authorName ?? `${firstName} ${lastName}`;

  const currentStock = currentListing.currentStock?.attributes?.quantity;
  const isOutOfStock = currentStock === 0;
  const showOutOfStockOverlay =
    !isBookable && isOutOfStock && !isPendingApproval && !isClosed && !isDraft;
  const hasStockManagementInUse =
    isProductOrder && foundListingTypeConfig?.stockType === STOCK_MULTIPLE_ITEMS;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const hasError = hasOpeningError || hasClosingError || hasDiscardingError;
  const thisListingInProgress =
    actionsInProgressListingId && actionsInProgressListingId.uuid === id;

  const onOverListingLink = () => {
    // Enforce preloading of ListingPage (loadable component)
    const { component: Page } = findRouteByRouteName('ListingPage', routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  const titleClasses = classNames(css.title, {
    [css.titlePending]: isPendingApproval,
    [css.titleDraft]: isDraft,
  });

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const statistics = useMemo(
    () => ({
      viewsCount,
      sentInquiriesCount,
      viewsWebsiteCount,
      viewsPhoneCount,
    }),
    [viewsCount, sentInquiriesCount, viewsWebsiteCount, viewsPhoneCount]
  );

  return (
    <div
      className={classNames(relativeWrapperClassName || css.relativePosition, {
        [css.faded]: isClosed,
      })}
    >
      <div
        className={classes}
        onClick={event => {
          if (disableOnClick) return;

          event.preventDefault();
          event.stopPropagation();

          // ManageListingCard contains links, buttons and elements that are working with routing.
          // This card doesn't work if <a> or <button> is used to wrap events that are card 'clicks'.
          //
          // NOTE: It might be better to absolute-position those buttons over a card-links.
          // (So, that they have no parent-child relationship - like '<a>bla<a>blaa</a></a>')
          history.push(createListingURL(routeConfiguration, listing));
        }}
        tabIndex={0}
        onMouseOver={onOverListingLink}
        onTouchStart={onOverListingLink}
      >
        <div className={css.clickWrapper}>
          <AspectRatioWrapper
            width={aspectWidth}
            height={aspectHeight}
            className={css.aspectRatioWrapper}
          >
            <ResponsiveImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
          <ManageListingStatus
            disableActions={disableActions}
            currentListing={currentListing}
            onListingStatusChange={onListingStatusChange}
          />
          {!hideLabel && (
            <>
              <ListingStatus currentListing={currentListing} />
              <ListingPromotion currentListing={currentListing} />
            </>
          )}

          {isOnSearchPage && (
            <>
              <div className={css.searchPageLabels}>
                {isRecommendedActive && (
                  <span className={css.recommendedIcon}>
                    <FormattedMessage id="General.listing.promotion.recommended" />
                  </span>
                )}
                {isHighlightActive && (
                  <span className={css.highlightedIcon}>
                    <FormattedMessage id="General.listing.promotion.highlighted" />
                  </span>
                )}
              </div>
              {isHighlightActive && <div className={css.highlightBar}></div>}
            </>
          )}

          <ShowClosedOverlayMaybe
            isClosed={isClosed}
            title={title}
            actionsInProgressListingId={actionsInProgressListingId}
            currentListingId={currentListing.id}
            currentListingState={currentListing.attributes.state}
            currentListingStatus={currentListing.attributes.publicData.status}
            onOpenListing={onOpenListing}
            intl={intl}
          />

          <ShowPendingApprovalOverlayMaybe
            isPendingApproval={isPendingApproval}
            title={title}
            intl={intl}
          />

          <ShowOutOfStockOverlayMaybe
            showOutOfStockOverlay={showOutOfStockOverlay}
            title={title}
            id={id}
            slug={slug}
            actionsInProgressListingId={actionsInProgressListingId}
            currentListingId={currentListing.id}
            onCloseListing={onCloseListing}
            hasStockManagementInUse={hasStockManagementInUse}
            intl={intl}
          />

          {thisListingInProgress ? (
            <Overlay>
              <IconSpinner />
            </Overlay>
          ) : hasError ? (
            <Overlay errorMessage={intl.formatMessage({ id: 'ManageListingCard.actionFailed' })} />
          ) : null}
        </div>

        <div className={css.info}>
          <span className={css.category}>
            <FormattedMessage
              id={`General.listing.type.${publicData?.category}.${publicData?.type}`}
            />
          </span>

          <div className={classNames(css.mainInfo, { [css.mainInfoSearchPage]: isOnSearchPage })}>
            <div className={classNames(css.titleWrapper, titleClasses)}>
              {formatTitle(displayTitle, MAX_LENGTH_FOR_WORDS_IN_TITLE)}
            </div>

            <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />

            <span className={css.location}>
              {locationYearLengthInfo || <FormattedMessage id="ManageListingCard.locationNotSet" />}
            </span>

            {!hideStats && !isDraft && <Statistics {...statistics} isPaused={isPaused} />}
            <div className={classNames(css.manageLinks, css.manageLinksAbsolute)}>
              <div className={css.manageLinksActions}>
                {!disableActions && (
                  <ManageListingLinks
                    id={id}
                    slug={slug}
                    type={editListingLinkType}
                    actionsInProgressListingId={actionsInProgressListingId}
                    currentListing={currentListing}
                    onCloseListing={onCloseListing}
                    isClosed={isClosed}
                    isDraft={isDraft}
                    onDiscardDraft={onDiscardDraft}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onPromoteListing={onPromoteListing}
                    onCancelListingPromotion={onCancelListingPromotion}
                    onOpenPromotionModal={onOpenPromotionModal}
                    promotion={{ plan: promotionPlan, expiresAt: promotedUntil }}
                  />
                )}
              </div>
              {isExceedingSubscriptionLimit && isDraft && (
                <span className={css.limitExceeded}>
                  <FormattedMessage id="ManageListingCard.exceededLimit" />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageListingCard;
